import { ConfigurationSourceDataMap, ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Metadata, Participant, SourceData, SourceDataEventStatus, Trial } from "@/api/dtos";
import { useMemo } from "react";
import { createEventConfiguration } from "../configuration/useEventConfiguration";

export enum EventExcludedReason {
    Disabled = 'The Study Event is disabled in the participant\'s allocation.',
    Status = 'The Study Event\'s Status is not included in the participant\'s allocation.'
}

export const createEvent = (
    metadata?: Metadata, 
    trialConfigurationEvents?: ConfigurationSourceDataMap['events'], 
    configurationEvents?: ConfigurationSourceDataMap['events'], 
    sourceDataEvents?: SourceData['events'], 
    trial?: Trial, 
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution,
    participant?: Participant,
    isGlobal?: boolean,
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    studyEventRepeat?: number,
    preview?: boolean,
    includeExcluded = false
) => {
    if (!studyEventRepeat) {
        return undefined;
    }

    const eventConfiguration = createEventConfiguration(
        metadata,
        trialConfigurationEvents,
        configurationEvents,
        trial,
        isGlobal,
        configurationSourceDataId,
        studyEventOid
    )

    if (!eventConfiguration) {
        return undefined;
    }

    let eventExcludeReason: EventExcludedReason | undefined = undefined;

    const sourceDataEvent = sourceDataEvents?.[studyEventOid!]?.[studyEventRepeat]

    if (!eventConfiguration.configuration?.enabled ||
        !eventConfiguration.configuration?.statuses ||
        !eventConfiguration.configuration.statuses.includes(sourceDataEvent?.status ?? SourceDataEventStatus.None)) {
        if (!includeExcluded) {
            return undefined;
        }

        eventExcludeReason = !eventConfiguration.configuration?.enabled
            ? EventExcludedReason.Disabled
            : EventExcludedReason.Status;
    }
    
    const link = `/events/${studyEventOid}/${studyEventRepeat}`;

    return {
        ...eventConfiguration,
        trial,
        masterGroup, 
        coordinatingGroup,
        institution,
        participant,
        preview,
        id: `${studyEventOid}-${studyEventRepeat}` as any as number,
        status: ConfigurationStatus.Enabled,
        studyEventRepeat: studyEventRepeat,
        sourceDataEvent: sourceDataEvent,
        previewLink: `/${trial?.oid}/preview/participants/${participant?.studyNumber}${link}`,
        dataLink: `/${trial?.oid}${institution ? `/institutions/${institution.code}` : ''}/participants/${participant?.studyNumber}${link}`,

        excluded: !!eventExcludeReason,
        exclusionReason: eventExcludeReason
    };
}

const useEvent = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    sourceData?: SourceData,
    trial?: Trial, 
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution,
    participant?: Participant,
    studyEventOid?: string,
    studyEventRepeat?: number,
    preview?: boolean,
    includeExcluded = false
) => {
    return useMemo(() => {
        return createEvent(
            metadata, 
            trialConfiguration?.events, 
            configuration?.events,
            sourceData?.events, 
            trial, 
            masterGroup,
            coordinatingGroup,
            institution,
            participant,
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id, 
            studyEventOid,
            studyEventRepeat,
            preview,
            includeExcluded
        );
    }, [
        metadata, 
        trialConfiguration?.events, 
        configuration?.events, 
        sourceData?.events, 
        trial, 
        masterGroup, 
        coordinatingGroup,
        institution, 
        participant, 
        studyEventOid, 
        studyEventRepeat,
        preview,
        includeExcluded
    ]);
};

export default useEvent;